import React, { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { Button, Card, CardContent, CardHeader, Select } from '../../components/ui'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { API_ENDPOINTS } from '../../config/api'
import { Plus, Edit, Trash2, Eye, X, Users, CheckCircle, Clock, AlertCircle, BarChart3 } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'

interface Training {
  id: string
  name: string
  type: string
  link?: string
  createdAt: string
  assignments: TrainingAssignment[]
}

interface TrainingAssignment {
  id: string
  userEmail: string
  status: string
  assignedAt: string
  completedAt?: string
}

interface TrainingStats {
  summary: {
    totalAssigned: number
    totalCompleted: number
    totalInProgress: number
    totalOverdue: number
    completionRate: string
  }
  completionOverTime: Array<{ month: string; completed: number }>
  perTraining: Array<{
    id: string
    name: string
    type: string
    assigned: number
    completed: number
    inProgress: number
    overdue: number
  }>
  perDepartment: Array<{
    department: string
    assigned: number
    completed: number
    completionRate: string
  }>
}

const GovernanceTraining: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [trainings, setTrainings] = useState<Training[]>([])
  const [stats, setStats] = useState<TrainingStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadTrainings()
    loadStats()
  }, [])

  useEffect(() => {
    loadTrainings()
  }, [typeFilter, searchQuery])

  const loadTrainings = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_TRAININGS, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to load trainings')
      const data = await res.json()
      setTrainings(data.trainings || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load trainings')
      console.error('[GovernanceTraining] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_TRAININGS_STATS, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setStats(data)
      }
    } catch (err) {
      console.error('[GovernanceTraining] Error loading stats:', err)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t('governance.training.deleteConfirm', 'Are you sure you want to delete this training?'))) {
      return
    }

    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_TRAINING(id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete training')
      }

      await loadTrainings()
      await loadStats()
    } catch (err: any) {
      alert(err.message || t('governance.training.deleteError', 'Failed to delete training'))
    }
  }

  const handleCreate = () => {
    setSelectedTraining(null)
    setCreateModalOpen(true)
  }

  const handleEdit = (training: Training) => {
    setSelectedTraining(training)
    setEditModalOpen(true)
  }

  const handleAssign = (training: Training) => {
    setSelectedTraining(training)
    setAssignModalOpen(true)
  }

  const handleSave = async () => {
    await loadTrainings()
    await loadStats()
    setCreateModalOpen(false)
    setEditModalOpen(false)
    setAssignModalOpen(false)
    setSelectedTraining(null)
  }

  const filteredTrainings = useMemo(() => {
    return trainings.filter(t => {
      if (typeFilter && t.type !== typeFilter) return false
      if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
      return true
    })
  }, [trainings, typeFilter, searchQuery])

  const uniqueTypes = useMemo(() => {
    return Array.from(new Set(trainings.map(t => t.type))).sort()
  }, [trainings])

  const typeFilterOptions = useMemo(
    () => [
      { value: '', label: t('governance.training.allTypes', 'All types') },
      ...uniqueTypes.map((type) => ({ value: type, label: type })),
    ],
    [uniqueTypes, t],
  )

  const COLORS = ['#56a3d9', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6']

  if (loading && trainings.length === 0) {
    return (
      <PageLayout pageScope="governance-training-page" title={t('governance.training.title', 'Security Awareness & Training')}>
        <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
      </PageLayout>
    )
  }

  return (
    <PageLayout pageScope="governance-training-page" title={t('governance.training.title', 'Security Awareness & Training')}
      actions={
        <div className={listStyles.headerActions}>
          <Button
            type="button"
            color="primary"
            onClick={handleCreate}
            icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
          >
            {t('governance.training.create', 'New Training')}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {stats && (
        <GovernanceListingSection title={t('governance.training.overview', 'Training Overview')}>
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
              <p className={listStyles.metricTileLabel}>{t('governance.training.assigned', 'Assigned')}</p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{stats.summary.totalAssigned}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
              <p className={listStyles.metricTileLabel}>{t('governance.training.completed', 'Completed')}</p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>{stats.summary.totalCompleted}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
              <p className={listStyles.metricTileLabel}>{t('governance.training.inProgress', 'In Progress')}</p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{stats.summary.totalInProgress}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
              <p className={listStyles.metricTileLabel}>{t('governance.training.overdue', 'Overdue')}</p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{stats.summary.totalOverdue}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
              <p className={listStyles.metricTileLabel}>{t('governance.training.completionRate', 'Completion rate')}</p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{stats.summary.completionRate}%</p>
            </div>
          </div>
        </GovernanceListingSection>
      )}

      {stats && (
        <div className={`${listStyles.chartsRow} ${listStyles.sectionSpacing}`}>
          <Card>
            <CardHeader>
              <h3 className={listStyles.chartCardTitle}>
                {t('governance.training.chartCompletionOverTime', 'Training completion over time')}
              </h3>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.completionOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="completed" stroke="#56a3d9" strokeWidth={2} name="Completed" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Per Training */}
          <Card>
            <CardHeader>
              <h3 className={listStyles.chartCardTitle}>
                {t('governance.training.chartByTraining', 'Completion by training')}
              </h3>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.perTraining.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="assigned" fill="#94a3b8" name="Assigned" />
                  <Bar dataKey="completed" fill="#10B981" name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Per Department */}
          {stats.perDepartment.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <h3 className={listStyles.chartCardTitle}>
                  {t('governance.training.chartByDepartment', 'Completion by department')}
                </h3>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.perDepartment}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="assigned" fill="#94a3b8" name="Assigned" />
                    <Bar dataKey="completed" fill="#10B981" name="Completed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <GovernanceListingSection
        title={t('governance.training.list', 'Training Programs')}
        description={t('governance.training.listSubtitle', 'Filter by type or name, then assign users or edit a program.')}
        toolbar={
          <div className={listStyles.filtersRow}>
            <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
              <SearchField
                label={t('governance.training.searchLabel', 'Search')}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('governance.training.searchPlaceholder', 'Search trainings...')}
                minWidth="14rem"
              />
            </div>
            <div className={listStyles.toolbarField}>
              <Select<string>
                label={t('governance.training.type', 'Type')}
                minWidth="12rem"
                options={typeFilterOptions}
                value={typeFilter === '' ? null : typeFilter}
                onChange={(v) => setTypeFilter(v ?? '')}
                placeholder={t('governance.training.allTypes', 'All types')}
              />
            </div>
            {(typeFilter || searchQuery) && (
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button
                  type="button"
                  color="outline"
                  size="small"
                  onClick={() => {
                    setTypeFilter('')
                    setSearchQuery('')
                  }}
                  icon={<X className={listStyles.badgeIcon} aria-hidden />}
                >
                  {t('governance.training.clearFilters', 'Clear')}
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.colName', 'Program')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.colType', 'Type')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.assigned', 'Assigned')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.completed', 'Completed')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.inProgress', 'In Progress')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.overdue', 'Overdue')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.colLink', 'Link')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.training.colActions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableLoadingRow colSpan={8}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              )}
              {!loading && filteredTrainings.length === 0 && (
                <TableEmptyRow colSpan={8}>
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" aria-hidden />
                  <p className="m-0">{t('governance.training.noTrainings', 'No trainings found. Create your first training to get started.')}</p>
                </TableEmptyRow>
              )}
              {!loading &&
                filteredTrainings.map((training) => {
                  const assigned = training.assignments.length
                  const completed = training.assignments.filter((a) => a.status === 'Completed').length
                  const inProgress = training.assignments.filter((a) => a.status === 'InProgress').length
                  const overdue = training.assignments.filter((a) => a.status === 'Overdue').length
                  return (
                    <tr key={training.id} className={listStyles.row}>
                      <td className={listStyles.td}>
                        <div className={listStyles.cellStrong}>{training.name}</div>
                      </td>
                      <td className={listStyles.td}>
                        <span className={`${listStyles.badge} ${listStyles.badgeInProgress}`}>{training.type}</span>
                      </td>
                      <td className={listStyles.td}>{assigned}</td>
                      <td className={`${listStyles.td} ${listStyles.textGreen}`}>{completed}</td>
                      <td className={`${listStyles.td} ${listStyles.textBlue}`}>{inProgress}</td>
                      <td className={`${listStyles.td} ${listStyles.textRed}`}>{overdue}</td>
                      <td className={listStyles.td}>
                        {training.link ? (
                          <a
                            href={training.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={listStyles.cellMuted}
                          >
                            {t('governance.training.openLink', 'Open')}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={listStyles.td}>
                        <div className={listStyles.actionsGap}>
                          <Button
                            type="button"
                            color="outline"
                            size="small"
                            onClick={() => handleAssign(training)}
                            iconOnly
                            className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-blue-600"
                            title={t('governance.training.assignUsers', 'Assign to users')}
                            icon={<Users className={listStyles.badgeIcon} aria-hidden />}
                          />
                          <Button
                            type="button"
                            color="outline"
                            size="small"
                            onClick={() => handleEdit(training)}
                            iconOnly
                            className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-green-600"
                            title={t('common.edit', 'Edit')}
                            icon={<Edit className={listStyles.badgeIcon} aria-hidden />}
                          />
                          <Button
                            type="button"
                            color="outline"
                            size="small"
                            onClick={() => handleDelete(training.id)}
                            iconOnly
                            className="border-0 bg-transparent p-1 shadow-none text-gray-600 hover:text-red-600"
                            title={t('common.delete', 'Delete')}
                            icon={<Trash2 className={listStyles.badgeIcon} aria-hidden />}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>

      {/* Modals */}
      {createModalOpen && (
        <CreateTrainingModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false)
            setSelectedTraining(null)
          }}
          onSave={handleSave}
        />
      )}

      {editModalOpen && selectedTraining && (
        <EditTrainingModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false)
            setSelectedTraining(null)
          }}
          onSave={handleSave}
          training={selectedTraining}
        />
      )}

      {assignModalOpen && selectedTraining && (
        <AssignTrainingModal
          isOpen={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false)
            setSelectedTraining(null)
          }}
          onSave={handleSave}
          training={selectedTraining}
        />
      )}
    </PageLayout>
  )
}

// Create Training Modal
const CreateTrainingModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}> = ({ isOpen, onClose, onSave }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    type: 'other',
    link: ''
  })
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      alert('Name is required')
      return
    }

    setSaving(true)
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_TRAININGS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to create training')
      }

      onSave()
    } catch (err: any) {
      alert(err.message || 'Failed to create training')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">New Training</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <Select
                className="w-full"
                minWidth="100%"
                options={[
                  { value: 'onboarding', label: 'Onboarding' },
                  { value: 'phishing', label: 'Phishing' },
                  { value: 'annual_training', label: 'Annual Training' },
                  { value: 'compliance', label: 'Compliance' },
                  { value: 'other', label: 'Other' },
                ]}
                value={formData.type}
                onChange={(v) => v != null && setFormData({ ...formData, type: v })}
                placeholder="Type"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Link (optional)</label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                className="w-full p-2 border rounded"
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" color="outline" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary" size="small" disabled={saving}>
              {saving ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Training Modal
const EditTrainingModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  training: Training
}> = ({ isOpen, onClose, onSave, training }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [formData, setFormData] = useState({
    name: training.name,
    type: training.type,
    link: training.link || ''
  })
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      alert('Name is required')
      return
    }

    setSaving(true)
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_TRAINING(training.id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to update training')
      }

      onSave()
    } catch (err: any) {
      alert(err.message || 'Failed to update training')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Edit Training</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <Select
                className="w-full"
                minWidth="100%"
                options={[
                  { value: 'onboarding', label: 'Onboarding' },
                  { value: 'phishing', label: 'Phishing' },
                  { value: 'annual_training', label: 'Annual Training' },
                  { value: 'compliance', label: 'Compliance' },
                  { value: 'other', label: 'Other' },
                ]}
                value={formData.type}
                onChange={(v) => v != null && setFormData({ ...formData, type: v })}
                placeholder="Type"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Link (optional)</label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                className="w-full p-2 border rounded"
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" color="outline" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary" size="small" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Assign Training Modal
const AssignTrainingModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  training: Training
}> = ({ isOpen, onClose, onSave, training }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [emails, setEmails] = useState('')
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const emailList = emails.split('\n').map(e => e.trim()).filter(e => e)
    if (emailList.length === 0) {
      alert('Please enter at least one email address')
      return
    }

    setSaving(true)
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_TRAINING_ASSIGN(training.id), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emails: emailList })
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to assign training')
      }

      const data = await res.json()
      alert(`Successfully assigned training to ${data.count} user(s)`)
      onSave()
    } catch (err: any) {
      alert(err.message || 'Failed to assign training')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Assign Training: {training.name}</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Email Addresses (one per line) *
              </label>
              <textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                className="w-full p-2 border rounded"
                rows={8}
                placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" color="outline" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary" size="small" disabled={saving}>
              {saving ? 'Assigning...' : 'Assign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GovernanceTraining
